import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchUserById, updateUserById } from '@feathr/blackbox';
import { Button, Fieldset, Icon, ImagePicker, Input, showError, toast } from '@feathr/components';
import { flattenError } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

import * as styles from './CompleteProfile.css';

interface ICompleteProfileProps {
  onComplete: () => void;
  userId: string;
}

function CompleteProfile({ onComplete, userId }: Readonly<ICompleteProfileProps>): JSX.Element {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [picture, setPicture] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [firstNameHadFocus, setFirstNameHadFocus] = useState(false);
  const [lastNameHadFocus, setLastNameHadFocus] = useState(false);

  // Fetch user data on mount to get default first/last name from invitation
  useEffect(() => {
    async function fetchUserData(): Promise<void> {
      try {
        const user = await fetchUserById(userId, false);
        setFirstName(user.first_name || '');
        setLastName(user.last_name || '');
        setPicture(user.picture);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch user data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchUserData();
  }, [userId]);

  const firstNameValidation = validate.single(firstName, {
    presence: { allowEmpty: false, message: '^First name is required.' },
  });
  const firstNameErrors = flattenError(firstNameValidation) ?? [];

  const lastNameValidation = validate.single(lastName, {
    presence: { allowEmpty: false, message: '^Last name is required.' },
  });
  const lastNameErrors = flattenError(lastNameValidation) ?? [];

  const hasErrors = firstNameErrors.length > 0 || lastNameErrors.length > 0;

  function handleFirstNameChange(value?: string): void {
    setFirstName(value ?? '');
  }

  function handleLastNameChange(value?: string): void {
    setLastName(value ?? '');
  }

  function handlePictureChange(url: string): void {
    setPicture(url);
  }

  function handleFirstNameBlur(): void {
    setFirstNameHadFocus(true);
  }

  function handleLastNameBlur(): void {
    setLastNameHadFocus(true);
  }

  function renderCustomTrigger(onClick: () => void): JSX.Element {
    return (
      <button
        aria-label={t('Upload profile picture')}
        className={styles.plusIcon}
        onClick={onClick}
        type={'button'}
      >
        <Icon.Add size={5} />
      </button>
    );
  }

  async function handleSubmit(): Promise<void> {
    if (hasErrors) {
      return;
    }

    setIsSaving(true);

    try {
      await updateUserById(
        userId,
        {
          first_name: firstName,
          last_name: lastName,
          picture,
        },
        false,
      );

      toast(t('Profile updated successfully.'), { type: 'success' });

      // Small delay to ensure backend processing completes
      setTimeout(() => {
        onComplete();
      }, 500);
    } catch (error) {
      showError(error, t);
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <div className={styles.loading}>{t('Loading...')}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.imagePickerWrapper}>
        <div className={styles.imageContainer}>
          <ImagePicker
            attribute={'picture'}
            customTrigger={renderCustomTrigger}
            hidePickerOptions={true}
            onPick={handlePictureChange}
            value={picture}
            variant={'rounded'}
          />
        </div>
        <div className={styles.imagePickerHelp}>{t('Upload a profile picture (optional)')}</div>
      </div>

      <Fieldset direction={'column'}>
        <Input
          label={t('First name')}
          maxLength={40}
          name={'first_name'}
          onBlur={handleFirstNameBlur}
          onChange={handleFirstNameChange}
          required={true}
          showCounter={firstName.length > 0}
          type={'text'}
          validationError={firstNameHadFocus ? firstNameErrors : []}
          value={firstName}
        />

        <Input
          label={t('Last name')}
          maxLength={40}
          name={'last_name'}
          onBlur={handleLastNameBlur}
          onChange={handleLastNameChange}
          required={true}
          showCounter={lastName.length > 0}
          type={'text'}
          validationError={lastNameHadFocus ? lastNameErrors : []}
          value={lastName}
        />
      </Fieldset>

      <div className={styles.actions}>
        <Button disabled={hasErrors || isSaving} onClick={handleSubmit} type={'primary'}>
          {t('Complete Setup')}
        </Button>
      </div>
    </div>
  );
}

export default CompleteProfile;
